/**
 * Foundation/Core — useMediaQuery hooks
 *
 * Hook responsive centralizzato per la Gruppo Impresa Suite.
 *
 * Basato su `window.matchMedia` con listener nativo (più efficiente di un
 * plain `resize` handler perche' il browser fa il debounce).
 *
 * Espone:
 *  - useMediaQuery(query): boolean · match arbitrario CSS media query
 *  - useIsMobile()      · true se viewport < md (768 px). Smartphone.
 *  - useIsCompact()     · true se viewport < lg (1024 px). Smartphone + tablet.
 *
 * Le soglie corrispondono ai breakpoint standard di Tailwind (`md`, `lg`).
 * Modificarli qui riflette in tutta la Suite senza toccare i singoli componenti.
 *
 * Utility 100% agnostica rispetto al dominio: puo' essere utilizzata da
 * qualsiasi Product Pack della Gruppo Impresa Suite.
 */
import { useEffect, useState } from "react";

export function useMediaQuery(query) {
  const get = () => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  };
  const [matches, setMatches] = useState(get);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const handler = (e) => setMatches(e.matches);
    setMatches(mql.matches);
    // Safari < 14 usa addListener/removeListener (deprecato ma compatibile).
    if (mql.addEventListener) {
      mql.addEventListener("change", handler);
      return () => mql.removeEventListener("change", handler);
    }
    mql.addListener(handler);
    return () => mql.removeListener(handler);
  }, [query]);

  return matches;
}

// Smartphone (< Tailwind `md`).
export function useIsMobile() {
  return useMediaQuery("(max-width: 767.98px)");
}

// Smartphone + tablet in portrait/landscape stretto (< Tailwind `lg`).
// Questa fascia (768–1023 px) e' spesso la piu' critica: la toolbar desktop e'
// troppo affollata, e su tablet in landscape una barra compatta con Sheet
// Dettagli offre la stessa usabilita' di una lavagna portatile.
export function useIsCompact() {
  return useMediaQuery("(max-width: 1023.98px)");
}
